import { useUpdateBriefFormV2 } from "@entities/brief/lib/hooks/useUpdateBriefFormV2";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import type { EditorHandle } from "@shared/ui/Editor/ui";
import { useEffect, useRef, type RefObject } from "react";
import { createBriefAutosave } from "./briefAutosaveController";

type UseCampaignBriefAutosaveParams = {
  briefId: string;
  editorRef: RefObject<EditorHandle | null>;
  nameRef: RefObject<string>;
  onSaved: () => void;
};

export function useCampaignBriefAutosave({
  briefId,
  editorRef,
  nameRef,
  onSaved,
}: UseCampaignBriefAutosaveParams) {
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  const briefIdRef = useRef(briefId);
  briefIdRef.current = briefId;
  const editorRefStable = editorRef;
  const nameRefStable = nameRef;

  const updateFormRef = useRef<
    (payload: {
      briefId: string;
      formV2: string;
      campaignName?: string;
      silent: true;
    }) => void
  >(() => undefined);

  const autosaveRef = useRef<ReturnType<typeof createBriefAutosave> | null>(
    null
  );
  if (!autosaveRef.current) {
    autosaveRef.current = createBriefAutosave({
      getHtml: () =>
        editorRefStable.current?.getHTML({ syncTokens: true }) ?? "",
      getName: () => nameRefStable.current ?? "",
      persist: ({ formV2, campaignName }) => {
        const id = briefIdRef.current;
        if (!id) return;
        updateFormRef.current({
          briefId: id,
          formV2,
          campaignName,
          silent: true,
        });
      },
    });
  }

  const { updateForm, isUpdating } = useUpdateBriefFormV2({
    onSuccess: (data, variables) => {
      autosaveRef.current?.markSaveSucceeded(
        variables.formV2,
        variables.campaignName ?? ""
      );

      const store = useBriefStore.getState();
      if (store.brief && store.briefId === variables.briefId) {
        store.setBrief(
          {
            ...store.brief,
            ...(data?.data ?? {}),
            name: variables.campaignName?.trim() || store.brief.name,
            formV2: variables.formV2,
          },
          variables.briefId
        );
      }

      onSavedRef.current();
    },
  });

  updateFormRef.current = updateForm;

  const wasUpdatingRef = useRef(false);
  useEffect(() => {
    if (isUpdating) {
      wasUpdatingRef.current = true;
      return;
    }
    if (wasUpdatingRef.current) {
      wasUpdatingRef.current = false;
      autosaveRef.current?.markSaveFailed();
    }
  }, [isUpdating]);

  useEffect(() => () => autosaveRef.current?.dispose(), []);

  const autosave = autosaveRef.current;

  return {
    isUpdating,
    scheduleSave: autosave.scheduleSave,
    saveImmediately: autosave.saveNow,
    pauseAutosaveForMedia: autosave.pause,
    saveAfterMediaUpload: autosave.resumeAndSave,
    resetSnapshot: autosave.resetSnapshot,
    clearPending: autosave.clearPending,
  };
}
